import { useState, DragEvent } from 'react';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';

interface DropZoneProps {
  onFileChange: (files: FileList | null) => void;
  children?: React.ReactNode;
}

export const DropZone = ({
  onFileChange,
  children,
}: DropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const target = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;

    if (!target.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileChange(files);
    }
  };

  return (
    <Container
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      <Overlay $isDragging={isDragging}>
        <OverlayText>
          <FormattedMessage
            id="dropFilesHereToUpload"
            defaultMessage="Drop files here to upload"
          />
        </OverlayText>
      </Overlay>
    </Container>
  );
};

const Container = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
});

const Overlay = styled('div')<{ $isDragging: boolean }>(
  ({ theme, $isDragging }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.blue50,
    border: `2px dashed ${theme.palette.blue500}`,
    borderRadius: theme.radii.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: $isDragging ? 'auto' : 'none',
    zIndex: 1000,
    opacity: $isDragging ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out',
  }),
);

const OverlayText = styled('div')({
  fontSize: '24px',
  fontWeight: 600,
  color: '#007AFF',
  textAlign: 'center',
  padding: '20px',
});
